import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const forgotPasswordSchema = z.object({
  email: z.string().email("E-mail inválido"),
});

export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordFormProps {
  onSubmit: (values: ForgotPasswordFormValues) => Promise<void>;
  loading?: boolean;
  error?: string | null;
  success?: boolean;
}

export function ForgotPasswordForm({
  onSubmit,
  loading,
  error,
  success,
}: ForgotPasswordFormProps) {
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
        data-testid="forgot-password-form"
      >
        {error && (
          <p className="text-sm font-light text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        {success && (
          <p className="text-sm font-light text-primary-700 dark:text-primary-300" role="status">
            Se o e-mail existir, você receberá instruções para redefinir sua senha.
          </p>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input type="email" autoComplete="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={loading || success} className="w-full">
          {loading ? "ENVIANDO..." : "ENVIAR LINK"}
        </Button>

        <p className="text-center text-sm font-light">
          <Link
            to="/login"
            className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-white transition-colors"
          >
            Voltar ao login
          </Link>
        </p>
      </form>
    </Form>
  );
}
